import lodashGet from "lodash/get";
import lodashHas from "lodash/has";
import camelCase from "lodash/camelCase";
import lodashMatchesProperty from "lodash/matchesProperty";
import lodashFilter from "lodash/filter";
import { propertyByLanguage } from "@/common/basic.utilities";

import {
  ENDPOINT_COLLABORATION_FOLDERS,
  ENDPOINT_COLLABORATION_EXPORT,
  ENDPOINT_COLLABORATION_SHARE,
  ENDPOINT_COLLABORATION_LINK,
} from "@/common/constants/api";
import {
  httpGet,
  httpPost,
  httpPut,
  httpPatch,
  httpDelete,
} from "@/services/api.service";
import { encodeAllURLParameters } from "@/common/api.utilities";
import { unjoinTopicScores } from "@/services/events/topics.service";
import { getCountriesDisplayMetadata } from "@/services/search/wod.service";
import { EVENT_ACTIONS, EVENT_CATEGORIES } from "@/common/constants/analytics";
import { pageTrackerLogger } from "@/common/pageTracker";
import { formatFileSize, upperCaseFirstLetter } from "@/common/basic.utilities";
import { getFormatted } from "@/common/date.utilities";
import { hasMaritimeAccess } from "@/services/user/user.service";
import {
  getWaterByCode,
  getCountryDisplayNameByCode,
} from "@/common/maps/utilities";

//TODO get this file from a dependency repo
const ANALYSIS_MAPPING = require("@/mappings/content_analysis_mapping.json");
const MARITIME_MAPPING = require("@/mappings/content_maritime_incident_mapping.json");
const SECURITY_MAPPING = require("@/mappings/content_security_incident_mapping.json");

export { _getFolders as getFolders };
export { _saveFolder as saveFolder };
export { _deleteFolder as deleteFolder };
export { _getArticlesFromFolder as getArticlesFromFolder };
export { _saveArticleToFolder as saveArticleToFolder };
export { _deleteArticleFromFolder as deleteArticleFromFolder };
export { _exportFolderDocuments as exportFolderDocuments };
export { _shareFolder as shareFolder };
export { _linkFolder as linkFolder };
export { _unlinkFolder as unlinkFolder };
export { _getFolderSummary as getFolderSummary };

const _getFolders = async (params) => {
  try {
    const RESULT = await httpGet({
      url: ENDPOINT_COLLABORATION_FOLDERS,
      noCache: true,
    });
    const { data } = JSON.parse(RESULT);

    if (data && data.data) {
      return data.data;
    }
    return false;
  } catch (error) {
    console.error("Unable to get folder: ", JSON.stringify(error));
    return false;
  }
};

const _saveFolder = async (params) => {
  if (!params || !params.folder) {
    console.error("Unable to save folder");
    return false;
  }
  if (params.folder.id) {
    return await _editFolder(params);
  }
  return await _addFolder(params);
};

const _addFolder = async (params) => {
  try {
    if (!params || !params.folder || params.folder.id) {
      console.error("Unable to add folder");
      return false;
    }

    const FOLDER = {
      folder_metadata: params.folder,
    };

    let url = ENDPOINT_COLLABORATION_FOLDERS;
    if (params.folder.parent) {
      url += `/${params.folder.parent}`;
    }

    let postRes = await httpPost({
      url: url,
      data: JSON.parse(JSON.stringify(FOLDER)),
      giveFailedResponse: true,
    });

    postRes = JSON.parse(postRes);
    if (postRes.data && postRes.data.results) {
      return postRes.data.results;
    }

    return true;
  } catch (error) {
    console.error("Unable to add folder: ", JSON.stringify(error));
    if (lodashHas(error, "data.message")) {
      throw new Error(lodashGet(error, "data.message"));
    }
    return false;
  }
};

const _editFolder = async (params) => {
  try {
    if (!lodashGet(params, "folder.id")) {
      console.error("Unable to edit folder");
      return false;
    }
    const FOLDER = {
      folder_metadata: params.folder,
    };
    await httpPatch({
      url: `${ENDPOINT_COLLABORATION_FOLDERS}/${params.folder.id}`,
      data: JSON.parse(JSON.stringify(FOLDER)),
      giveFailedResponse: true,
    });
    return true;
  } catch (error) {
    console.error("Unable to edit folder: ", JSON.stringify(error));
    if (lodashHas(error, "data.message")) {
      throw new Error(lodashGet(error, "data.message"));
    }
    return false;
  }
};

const _deleteFolder = async (params) => {
  try {
    if (!lodashGet(params, "folder.id")) {
      console.error("Unable to delete folder");
      return false;
    }
    if (
      lodashGet(params, "folder.children") &&
      Array.isArray(params.folder.children) &&
      params.folder.children[0].hasOwnProperty("id")
    ) {
      console.error("Unable to delete folder due to children");
      return false;
    }
    await httpDelete({
      url: `${ENDPOINT_COLLABORATION_FOLDERS}/${params.folder.id}`,
      noCache: true,
    });
    return true;
  } catch (error) {
    console.error("Unable to delete folder: ", JSON.stringify(error));
    return false;
  }
};

const _getArticlesFromFolder = async (params) => {
  try {
    if (lodashGet(params, "folder.id")) {
      if (!params.start && !params.end) {
        params.noStartEnd = true;
      }
      let folderId = params.folder.id;
      if (params.folder.hasOwnProperty("originalFolderId")) {
        // if folder is linked
        folderId = params.folder.originalFolderId;
      }
      const QUERY = encodeAllURLParameters({
        baseEndpoint: `${ENDPOINT_COLLABORATION_FOLDERS}/${folderId}`,
        params: params,
      });
      const RESULT = await httpGet({
        url: QUERY,
        noCache: true,
      });
      const { data } = JSON.parse(RESULT);

      if (data && data.data && data.total) {
        const DOCUMENTS = data.data;
        await _formatDocuments(DOCUMENTS);
        return {
          data: data.data,
          total: data.total,
        };
      }
      return false;
    }
    return false;
  } catch (error) {
    console.error(
      "Unable to get articles from folder: ",
      JSON.stringify(error)
    );
    return false;
  }
};

const _saveArticleToFolder = async (params) => {
  if (!lodashGet(params, "folder.id") || !lodashGet(params, "article")) {
    console.error("Unable to save article");
    return false;
  }

  if (params.article.id) {
    return await _editArticleInFolder(params);
  }
  return await _addArticleToFolder(params);
};

const _addArticleToFolder = async (params) => {
  try {
    if (
      !lodashGet(params, "folder.id") ||
      !lodashGet(params, "article.properties.formatted")
    ) {
      console.error("Unable to add article");
      return false;
    }

    let data = {};

    const DOCUMENT = _formatArticle(params.article.properties);
    data.document = DOCUMENT;

    if (!params.doc_metadata) {
      params.doc_metadata = {
        read: false, // default read to false when first added
      };
    }

    if (lodashHas(params, "article.properties.scores.tone")) {
      data.document.scores = {
        tone: lodashGet(params, "article.properties.scores.tone"),
      };
    }

    if (lodashGet(params, "article.properties.formatted.topic_scores")) {
      params.doc_metadata.topic_scores =
        params.article.properties.formatted.topic_scores;
      if (lodashGet(params, "article.properties.formatted.topic_scores_v2")) {
        params.doc_metadata.topic_scores_v2 =
          params.article.properties.formatted.topic_scores_v2;
      } else {
        params.doc_metadata = unjoinTopicScores(
          params.article.properties.formatted
        );
      }
    } else if (
      lodashGet(params, "article.properties.formatted.topic_scores_v2")
    ) {
      params.doc_metadata.topic_scores_v2 =
        params.article.properties.formatted.topic_scores_v2;
    }

    if (params.doc_metadata) {
      data.doc_metadata = params.doc_metadata;
    }

    let folderId = params.folder.id;
    if (lodashGet(params, "folder.originalFolderId")) {
      folderId = params.folder.originalFolderId;
    }

    await httpPut({
      url: `${ENDPOINT_COLLABORATION_FOLDERS}/${folderId}`,
      data: JSON.parse(JSON.stringify(data)),
      giveFailedResponse: true,
    });

    _handlePageTrackerEvent(
      EVENT_CATEGORIES.collaboration,
      EVENT_ACTIONS.clicked,
      "Add Content to Folder"
    );

    return true;
  } catch (error) {
    console.error("Unable to add article: ", JSON.stringify(error));
    if (lodashHas(error, "data.message")) {
      throw new Error(lodashGet(error, "data.message"));
    }
    return false;
  }
};

const _editArticleInFolder = async (params) => {
  try {
    if (!lodashGet(params, "folder.id") || !lodashGet(params, "article.id")) {
      console.error("Unable to edit article");
      return false;
    }

    let data = {
      doc_metadata: params.doc_metadata,
    };

    let folderId = params.folder.id;
    if (lodashGet(params, "folder.originalFolderId")) {
      folderId = params.folder.originalFolderId;
    }

    await httpPatch({
      url: `${ENDPOINT_COLLABORATION_FOLDERS}/${folderId}/${params.article.id}`,
      data: JSON.parse(JSON.stringify(data)),
      giveFailedResponse: true,
    });

    return true;
  } catch (error) {
    console.error("Unable to save article: ", JSON.stringify(error));
    if (lodashHas(error, "data.message")) {
      throw new Error(lodashGet(error, "data.message"));
    }
    return false;
  }
};

const _deleteArticleFromFolder = async (params) => {
  try {
    if (!lodashGet(params, "folder.id") || !lodashGet(params, "article.id")) {
      console.error("Unable to delete article from folder");
      return false;
    }
    const PATH = `${params.folder.id}/${params.article.id}`;
    await httpDelete({
      url: `${ENDPOINT_COLLABORATION_FOLDERS}/${PATH}`,
      noCache: true,
    });
    return true;
  } catch (error) {
    console.error(
      "Unable to delete article from folder:",
      JSON.stringify(error)
    );
    return false;
  }
};

const _exportFolderDocuments = async (params) => {
  try {
    if (!params || !params.folder || !params.folder.id || !params.type) {
      console.error("Unable to export folder documents");
      return false;
    }

    const URL = `${ENDPOINT_COLLABORATION_EXPORT}/${params.type}/${params.folder.id}`;

    const RESPONSE = await httpGet({
      url: URL,
      noCache: true,
    });

    if (RESPONSE) {
      const RESULTS = JSON.parse(RESPONSE);
      if (lodashGet(RESULTS, "data.data")) {
        return RESULTS.data.data;
      }
    }

    return false;
  } catch (error) {
    console.error("Unable to export folder documents: ", JSON.stringify(error));
    return false;
  }
};

const _shareFolder = async (params) => {
  try {
    if (!lodashGet(params, "folder.id")) {
      console.error("Unable to share folder");
      return false;
    }

    const FOLDER = {
      folder_metadata: params.folder,
    };

    await httpPatch({
      url: `${ENDPOINT_COLLABORATION_SHARE}/${params.folder.id}`,
      data: JSON.parse(JSON.stringify(FOLDER)),
      giveFailedResponse: true,
    });

    return true;
  } catch (error) {
    console.error("Unable to share folder: ", JSON.stringify(error));
    return false;
  }
};

const _linkFolder = async (params) => {
  try {
    if (!lodashGet(params, "folder.id")) {
      console.error("Unable to link folder");
      return false;
    }

    const FOLDER = {
      folder_metadata: params.folder,
    };

    await httpPatch({
      url: `${ENDPOINT_COLLABORATION_LINK}/${params.folder.id}`,
      data: JSON.parse(JSON.stringify(FOLDER)),
      giveFailedResponse: true,
    });

    return true;
  } catch (error) {
    console.error("Unable to link folder: ", JSON.stringify(error));
    return false;
  }
};

const _unlinkFolder = async (params) => {
  try {
    if (
      !lodashGet(params, "folder.id") ||
      !lodashGet(params, "folder.originalFolderId") ||
      !lodashGet(params, "folder.parent")
    ) {
      console.error("Unable to unlink folder");
      return false;
    }

    await httpDelete({
      url: `${ENDPOINT_COLLABORATION_LINK}/${params.folder.originalFolderId}/${params.folder.parent}`,
      noCache: true,
    });

    return true;
  } catch (error) {
    console.error("Unable to unlink folder: ", JSON.stringify(error));
    return false;
  }
};

// article === item.properties.formatted from wod.services.js
const _formatArticle = (allProperties) => {
  const article = allProperties.formatted;

  let properties = {
    ..._formatVerified(article),
    ..._formatAnalysis(allProperties),
  };
  if (article.type) {
    // twitter or news
    if (article.type === "news") {
      properties["source"] = "news-all";
    } else {
      properties["source"] = article.type;
    }
  }

  if (article.occurred && !properties["@timestamp"]) {
    properties["@timestamp"] = article.occurred;
  }
  if (article.esId && !properties["id"]) {
    properties["id"] = article.esId;
  }
  if (article.img && !properties["imageUrl"]) {
    properties["imageUrl"] = article.img;
  }
  if (article.keywords && !properties["keywords"]) {
    properties["keywords"] = article.keywords;
  }
  if (article.language && !properties["language"]) {
    properties["language"] = article.language;
  }
  if (article.source && !properties["origin"]) {
    properties["origin"] = article.source;
  }
  if (article.summary && !properties["summary"]) {
    properties["summary"] = article.summary;
  }
  if (article.title && !properties["title"]) {
    properties["title"] = article.title;
  }
  if (article.link && !properties["url"]) {
    properties["url"] = article.link;
  }
  if (article.sourceVeracity && !properties["veracity"]) {
    properties["veracity"] = article.sourceVeracity;
  }
  if (article.translated && !properties["translated"]) {
    properties["translated"] = article.translated;
  }
  if (article.topic_scores && !properties["topic_scores"]) {
    properties["topic_scores"] = article.topic_scores;
  }
  if (article.topic_scores_v2 && !properties["topic_scores_v2"]) {
    properties["topic_scores_v2"] = article.topic_scores_v2;
  }

  //Save International SOS properties
  if (article.travelSource && !properties.travelSource) {
    properties.travelSource = article.travelSource;
  }
  if (article.travelLevel && !properties.travelLevel) {
    properties.travelLevel = article.travelLevel;
  }
  if (article.travelCategories && !properties.travelCategories) {
    properties.travelCategories = article.travelCategories;
  }

  //PMESII properties (formatted isn't in the format we'd like to store, so pulling from allProperties)
  if (
    Array.isArray(allProperties.influencees) &&
    allProperties.influencees.length > 0
  ) {
    properties.influencees = allProperties.influencees;
  }
  if (
    Array.isArray(allProperties.influencers) &&
    allProperties.influencers.length > 0
  ) {
    properties.influencers = allProperties.influencers;
  }
  if (allProperties.influence_explanation) {
    properties.influence_explanation = allProperties.influence_explanation;
  }

  if (
    article.location &&
    Array.isArray(article.location) &&
    article.location.length === 2
  ) {
    properties["location"] = {
      lon: article.location[0],
      lat: article.location[1],
    };
  }
  return properties;
};

// Format response article to verified event object
const _formatVerified = (article) => {
  let mapping = null;
  const remappedFields = {};
  const additionalFields = {
    categories: "labeled_categories",
    topic_scores_v2: "topic_scores_v2",
  };

  if (article.type === "maritime") {
    mapping = MARITIME_MAPPING;
  } else if (article.type === "political") {
    mapping = SECURITY_MAPPING;
  } else {
    return {};
  }
  let properties = {};

  for (const [key, value] of Object.entries(additionalFields)) {
    if (article[key]) {
      properties[value] = article[key];
    }
  }

  for (const [field, info] of Object.entries(mapping)) {
    let camelCaseField = camelCase(field);
    if (remappedFields.hasOwnProperty(field) && article.hasOwnProperty(field)) {
      properties[remappedFields[field]] = article[camelCaseField];
    } else if (article[camelCaseField]) {
      properties[field] = article[camelCaseField];
    }
  }
  return properties;
};

// Format UI's Analysis object into format for colaboration endpoint.
// Collab mapping uses underscores
const _formatAnalysis = (allProperties) => {
  if (
    allProperties.formatted.type === "analysis" ||
    allProperties.source === "analysis"
  ) {
    const remappedFields = [
      {
        source: "title",
        formattedFieldName: "title",
        collabMapping: "title_multi_language",
      },
      {
        source: "summary",
        formattedFieldName: "summary",
        collabMapping: "summary_multi_language",
      },
      {
        source: "risk_types",
        formattedFieldName: "risks",
        collabMapping: "risk_types",
      },
    ];
    const additionalFields = {};
    const mapping = ANALYSIS_MAPPING;
    let collabProperties = {};
    collabProperties.summary_multi_language = [];
    if (allProperties.hasOwnProperty("sanitizedSummary")) {
      collabProperties.summary_multi_language = allProperties.sanitizedSummary;
    } else {
      let copy = JSON.parse(JSON.stringify(allProperties.sanitizedBody));
      for (const key in copy) {
        copy[key] = copy[key].substring(0, 300);
      }
      collabProperties.summary_multi_language = copy;
    }

    for (const [field, info] of Object.entries(mapping)) {
      let remappedFieldObj = remappedFields.find((m) => {
        return m.source === field;
      });
      let formattedFieldName = remappedFieldObj
        ? remappedFieldObj.formattedFieldName
        : camelCase(field);
      let collabField = remappedFieldObj
        ? remappedFieldObj.collabMapping
        : field;
      if (!collabProperties[collabField]) {
        if (allProperties.hasOwnProperty(formattedFieldName)) {
          collabProperties[collabField] = allProperties[formattedFieldName];
        } else if (allProperties.formatted.hasOwnProperty(formattedFieldName)) {
          collabProperties[collabField] =
            allProperties.formatted[formattedFieldName];
        }
      }
    }

    return collabProperties;
  } else {
    return {};
  }
};

const _formatDocuments = async (documents) => {
  const LEN = documents.length;
  for (let i = 0; i < LEN; i++) {
    let document = documents[i];
    await _formatDocument(document);
  }
};

// _formatDocument maps the http response from getArticlesFromFolder to UI objects
const _formatDocument = async (item) => {
  if (item) {
    item.properties = {
      formatted: {
        documentId: item.id ? item.id : null,
        read: item.doc_metadata && item.doc_metadata.read,
        comments: item.doc_metadata && item.doc_metadata.comments,
        color: item.doc_metadata && item.doc_metadata.color,
      },
    };
    if (item.document) {
      if (item.document.source) {
        if (item.document.source === "news-all") {
          item.document.source = "news";
        }
        item.properties.formatted.type = item.document.source || null;
        item.properties.formatted.source = item.document.source || null;
      }
      item.properties.formatted.occurred = item.document["@timestamp"]
        ? item.document["@timestamp"]
        : null;
      item.properties.formatted.esId = item.document.id
        ? item.document.id
        : null;
      item.properties.formatted.title = item.document.title
        ? item.document.title
        : null;
      item.properties.formatted.summary = item.document.summary
        ? item.document.summary
        : null;
      item.properties.formatted.link = item.document.url
        ? item.document.url
        : null;
      item.properties.formatted.img = item.document.imageUrl
        ? item.document.imageUrl
        : null;
      item.properties.formatted.source = item.document.origin
        ? item.document.origin
        : null;
      item.properties.formatted.keywords = item.document.keywords
        ? item.document.keywords
        : null;

      item.properties.formatted.translated = item.document.translated
        ? item.document.translated
        : null;

      item.properties.formatted.language = null;
      if (item.document.language) {
        item.properties.formatted.language = item.document.language;
        if (item.document.language.toLowerCase() === "en") {
          item.properties.formatted.translated = true;
        }
      }

      item.properties.formatted.sourceVeracity = item.document.veracity
        ? item.document.veracity
        : null;
      item.properties.formatted.topic_scores = item.doc_metadata.topic_scores
        ? item.doc_metadata.topic_scores
        : null;
      item.properties.formatted.location = item.document.location;

      if (item.document["@updated"]) {
        item.properties.formatted.updated = getFormatted(
          item.document["@updated"]
        );
      }

      //International SOS properties
      item.properties.formatted.travelSource =
        item.document.travelSource || null;
      item.properties.formatted.travelLevel = item.document.travelLevel || null;
      item.properties.formatted.travelCategories =
        item.document.travelCategories || null;

      //PMESII properties
      await reformatCollabApiPmesii(item);

      await _formatAnalysisDoc(item);
    }
  }
};

const _formatAnalysisDoc = async (item) => {
  if (lodashGet(item, "document.source") === "analysis") {
    item.properties.formatted.language = "en";
    if (lodashGet(item, "document.body")) {
      item.properties.formatted.body = propertyByLanguage(
        item.document.body,
        item.properties.formatted.language
      );
    }
    if (lodashGet(item, "document.sanitized_body")) {
      item.properties.formatted.sanitizedBody = propertyByLanguage(
        item.document.sanitized_body,
        item.properties.formatted.language
      );
    }
    if (lodashGet(item, "document.sanitized_summary")) {
      item.properties.formatted.summary = propertyByLanguage(
        item.document.sanitized_summary,
        item.properties.formatted.language
      );
    }
    if (lodashGet(item, "document.summary_multi_language")) {
      item.properties.formatted.summary = propertyByLanguage(
        item.document.summary_multi_language,
        item.properties.formatted.language
      );
    }
    if (lodashGet(item, "document.published_date")) {
      item.properties.formatted.published = getFormatted(
        item.document.published_date
      );
    }
    if (
      hasMaritimeAccess() &&
      item.document?.waters &&
      item.document?.waters.length
    ) {
      item.properties.formatted.waters = item.document.waters.map((water) => {
        const formattedWater = { ...water };
        if (!water.name && water.code) {
          formattedWater.name = getWaterByCode(water.code)?.name[0]?.text;
        }
        return formattedWater;
      });
    }
    if (lodashGet(item, "document.countries")) {
      const countries = item.document.countries.map((country) => {
        return lodashFilter(
          country.name,
          lodashMatchesProperty(
            "languageCode",
            item.properties.formatted.language
          )
        )[0].text;
      });
      const countriesWithFlag = item.document.countries.map((country) => {
        const flagUrl = country.flagUrl;
        const code = country.code;
        const countryName = lodashFilter(
          country.name,
          lodashMatchesProperty(
            "languageCode",
            item.properties.formatted.language
          )
        )[0].text;
        let countryText = `<a data-country-code="${code}">`;
        if (country.code) {
          countryText += `<img src="${flagUrl}" height="14" class="ml-1" style="border:1px solid #d1d1d1; border-radius:7px; position:relative; top:3px;" data-country-code="${code}" />`;
        }
        countryText += ` <span class="mr-1" data-country-code="${code}">${countryName}</span></a>`;
        return countryText;
      });
      item.properties.formatted.countries = countries.join(", ");
      item.properties.formatted.countriesWithFlag = countriesWithFlag.join(" ");
      item.properties.formatted.countriesDisplayMetadata = await getCountriesDisplayMetadata(
        { properties: { countries: item.document.countries } }
      );
    }
    if (lodashGet(item, "document.risk_types")) {
      const risks = item.document["risk_types"].map((risk) => {
        return upperCaseFirstLetter(risk.name);
      });
      item.properties.formatted.risks = risks.join(", ");
    }
    if (lodashGet(item, "document.attachments")) {
      const attachments = Object.entries(item.document.attachments).map(
        (entry) => {
          const [lang, attachmentArr] = entry;
          const attachment = attachmentArr[0];
          if (attachment && attachment.url) {
            const type = attachment.url.substring(attachment.url.length - 3);
            let size = attachment.size;
            let sizeFormatted = null;
            if (size) {
              sizeFormatted = formatFileSize(size, 0);
            }
            return {
              url: attachment.url,
              type: type.toLowerCase(),
              author: attachment.author || null,
              title: attachment.title || null,
              size: attachment.size || null,
              sizeFormatted: sizeFormatted,
              language: lang,
            };
          }
        }
      );
      item.properties.formatted.attachments = attachments;
    }
    if (item.document.title_multi_language) {
      item.properties.formatted.title = propertyByLanguage(
        item.document.title_multi_language,
        item.properties.formatted.language
      );
    }
  }
};

const _getFolderSummary = async (params) => {
  try {
    const response = await httpGet({
      url: `${ENDPOINT_COLLABORATION_FOLDERS}/${params.folder.id}/summary`,
      noCache: true,
      giveFailedResponse: true,
    });

    if (response) {
      const results = JSON.parse(response);
      if (lodashGet(results, "data.data")) {
        return results.data.data;
      }
    }

    return false;
  } catch (error) {
    console.error("There was an error retrieving the folder summary.");
    console.error(error);
    return false;
  }
};

const _handlePageTrackerEvent = (category, action, name) => {
  let _paq = (window._paq = window._paq || []);
  _paq.push(["trackEvent", category, action, name]);
  pageTrackerLogger(
    "Page Tracker Event " + category + " " + action + " " + name + " logged."
  );
};

const reformatCollabApiPmesii = async (item) => {
  if (!item.properties.formatted) {
    feature.formatted = {};
  }
  if (lodashGet(item, "document.influencees")) {
    const influencees = (
      await Promise.all(
        item.document.influencees.map(async (influencee) => {
          return getCountryDisplayNameByCode(influencee);
        })
      )
    ).join(", ");
    item.properties.influencees = item.document.influencees;
    item.properties.formatted.influencees = influencees;
  }
  if (lodashGet(item, "document.influencers")) {
    const influencers = (
      await Promise.all(
        item.document.influencers.map(async (influencer) => {
          return getCountryDisplayNameByCode(influencer);
        })
      )
    ).join(", ");
    item.properties.influencers = item.document.influencers;
    item.properties.formatted.influencers = influencers;
  }
  if (lodashGet(item, "document.influence_explanation")) {
    item.properties.influence_explanation = item.document.influence_explanation;
  }
};
